import React, { createContext, useReducer, useEffect } from 'react';
import storageService from '../services/storageService';
import { generateDocumentColor } from '../utils/colorUtils';

// Create context
export const DocumentContext = createContext();

// Initial state
const initialState = {
  documents: [], // Array of document objects
  activeDocumentId: null, // Currently selected document
  isLoading: false,
  error: null
};

// Action types
export const ActionTypes = {
  LOAD_DOCUMENTS: 'LOAD_DOCUMENTS',
  CREATE_DOCUMENT: 'CREATE_DOCUMENT',
  OPEN_DOCUMENT: 'OPEN_DOCUMENT',
  UPDATE_DOCUMENT: 'UPDATE_DOCUMENT',
  CLOSE_DOCUMENT: 'CLOSE_DOCUMENT',
  SET_ACTIVE_DOCUMENT: 'SET_ACTIVE_DOCUMENT',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR'
};

// Reducer function
function documentReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
        error: null
      };
      
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
      
    case ActionTypes.LOAD_DOCUMENTS:
      return {
        ...state,
        documents: action.payload,
        activeDocumentId: action.payload.length > 0 
          ? (state.activeDocumentId && action.payload.some(doc => doc.id === state.activeDocumentId) 
            ? state.activeDocumentId 
            : action.payload[0].id)
          : null,
        isLoading: false,
        error: null
      };
      
    case ActionTypes.CREATE_DOCUMENT: {
      const newDocument = {
        ...storageService.createDocument(action.payload),
        colorCode: generateDocumentColor(action.payload.title || 'Untitled Document')
      };
      
      return {
        ...state,
        documents: [...state.documents, newDocument],
        activeDocumentId: newDocument.id,
        isLoading: false,
        error: null
      };
    }
    
    case ActionTypes.OPEN_DOCUMENT: {
      // Check if document already exists
      const existingDoc = state.documents.find(doc => doc.id === action.payload.id);
      
      if (existingDoc) {
        // Document already open, just set it as active
        return {
          ...state,
          activeDocumentId: existingDoc.id,
          isLoading: false,
          error: null
        };
      }
      
      // Add the new document and set it as active
      const newDocument = {
        ...action.payload,
        colorCode: generateDocumentColor(action.payload.title || 'Untitled Document')
      };
      
      return {
        ...state,
        documents: [...state.documents, newDocument],
        activeDocumentId: newDocument.id,
        isLoading: false,
        error: null
      };
    }
    
    case ActionTypes.UPDATE_DOCUMENT:
      return {
        ...state,
        documents: state.documents.map(doc => 
          doc.id === action.payload.id 
            ? { ...doc, ...action.payload.updates, lastModified: new Date().toISOString() } 
            : doc
        ),
        isLoading: false,
        error: null
      };
    
    case ActionTypes.CLOSE_DOCUMENT: {
      // Filter out the document to close
      const remainingDocs = state.documents.filter(doc => doc.id !== action.payload);
      
      // Determine the new active document
      let newActiveId = state.activeDocumentId;
      
      if (state.activeDocumentId === action.payload) {
        // The closed document was active, select a new one
        newActiveId = remainingDocs.length > 0 ? remainingDocs[0].id : null;
      }
      
      return {
        ...state,
        documents: remainingDocs,
        activeDocumentId: newActiveId,
        isLoading: false,
        error: null
      };
    }
    
    case ActionTypes.SET_ACTIVE_DOCUMENT:
      return {
        ...state,
        activeDocumentId: action.payload,
        isLoading: false,
        error: null
      };
      
    default:
      return state;
  }
}

// Provider component
export function DocumentProvider({ children }) {
  const [state, dispatch] = useReducer(documentReducer, initialState);
  
  // Load documents from storage on mount
  useEffect(() => {
    const loadDocuments = async () => {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      
      try {
        const savedDocuments = await storageService.loadDocuments();
        
        // Add color codes to documents if they don't have them
        const documentsWithColors = savedDocuments.map(doc => ({
          ...doc,
          colorCode: doc.colorCode || generateDocumentColor(doc.title || 'Untitled Document')
        }));
        
        dispatch({ 
          type: ActionTypes.LOAD_DOCUMENTS, 
          payload: documentsWithColors 
        });
      } catch (error) {
        console.error('Failed to load documents', error);
        dispatch({ 
          type: ActionTypes.SET_ERROR, 
          payload: 'Failed to load documents' 
        });
      }
    };
    
    loadDocuments();
  }, []);
  
  // Save documents whenever they change
  useEffect(() => {
    const saveDocuments = async () => {
      if (state.documents.length > 0) {
        try {
          await storageService.saveDocuments(state.documents);
        } catch (error) {
          console.error('Failed to save documents', error);
        }
      }
    };
    
    saveDocuments();
  }, [state.documents]);
  
  // Save app state for persistence
  useEffect(() => {
    const saveAppState = async () => {
      try {
        await storageService.saveAppState({
          activeDocumentId: state.activeDocumentId
        });
      } catch (error) {
        console.error('Failed to save app state', error);
      }
    };
    
    saveAppState();
  }, [state.activeDocumentId]);
  
  return (
    <DocumentContext.Provider value={{ state, dispatch }}>
      {children}
    </DocumentContext.Provider>
  );
}
